// @ts-nocheck
/**
 * Restyles Recharge's injected `.rc-bundle-item` cart placeholder.
 * Also dedupes against the theme Liquid row and removes free gifts with the bundle.
 */

import { CartUpdateEvent } from '@theme/events';

const CHECKMARK_SVG = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark" aria-hidden="true"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const DELETE_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon icon-delete" aria-hidden="true"><path d="M6.5 2.5h3M2.5 4h11M5.5 4l.4 8.2a1 1 0 0 0 1 .8h2.2a1 1 0 0 0 1-.8L10.5 4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/**
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string} raw
 * @returns {string}
 */
function formatFrequency(raw) {
  if (!raw) return '';
  const everyMatch = raw.match(/every\s+(\d+)\s*(week|month|day)/i);
  if (everyMatch) {
    const n = everyMatch[1];
    const unit = String(everyMatch[2] || 'week').toLowerCase();
    return `Every ${n} ${n === '1' ? unit : `${unit}s`}`;
  }
  const weekMatch = raw.match(/(\d+)\s*week/i);
  if (weekMatch) {
    const n = weekMatch[1];
    return `Every ${n} week${n === '1' ? '' : 's'}`;
  }
  return raw;
}

/**
 * @param {string} raw
 * @returns {string}
 */
function parseDiscountPct(raw) {
  const match = String(raw || '').match(/(\d+)\s*%/);
  return match && match[1] ? match[1] : '';
}

/**
 * @returns {string}
 */
function cartRoot() {
  return window?.Shopify?.routes?.root || '/';
}

/**
 * Always fetch a fresh cart — cached cart misses newly added free gifts.
 * @returns {Promise<object|null>}
 */
async function fetchCartFresh() {
  try {
    const res = await fetch(`${cartRoot()}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
    return res.ok ? res.json() : null;
  } catch {
    return null;
  }
}

/**
 * Prefer theme Liquid row; drop Recharge's injected duplicate.
 * @param {ParentNode} [root]
 */
function dedupeBundleRows(root = document) {
  if (!root.querySelector('.cart-items__table-row--rc-bundle')) return;
  root.querySelectorAll('tr:not(.cart-items__table-row--rc-bundle) .rc-bundle-item').forEach((el) => {
    el.closest('tr')?.remove();
  });
}

/**
 * Strip Recharge's inline `<style>` that fights theme CSS.
 * @param {HTMLElement} el
 */
function removeInjectedStyles(el) {
  const tr = el.closest('tr');
  if (!tr) return;

  let node = tr.previousSibling;
  while (node) {
    const current = node;
    node = node.previousSibling;

    if (current.nodeType === Node.COMMENT_NODE) {
      current.parentNode?.removeChild(current);
      continue;
    }

    if (current.nodeType === Node.TEXT_NODE && !String(current.textContent || '').trim()) {
      continue;
    }

    if (
      current.nodeType === Node.ELEMENT_NODE &&
      /** @type {HTMLElement} */ (current).tagName === 'STYLE' &&
      String(current.textContent || '').includes('.rc-bundle-item')
    ) {
      current.parentNode?.removeChild(current);
    }
    break;
  }
}

/**
 * @param {string} href
 * @returns {string[]}
 */
function keysFromRemoveHref(href) {
  if (!href) return [];
  return [...href.matchAll(/updates\[([^\]]+)\]=0/g)]
    .map((match) => match[1])
    .filter(Boolean);
}

/**
 * Collect every cart line key that should clear with a Recharge bundle remove,
 * including free gifts / add-ons linked by `_rb_id` (and parent→child links).
 * @param {any} cart
 * @param {string[]} seedKeys
 * @returns {Set<string>}
 */
function collectBundleRemoveKeys(cart, seedKeys) {
  /** @type {Set<string>} */
  const keys = new Set(seedKeys.filter(Boolean));
  const items = cart?.items || [];
  if (!items.length || !keys.size) return keys;

  /** @type {Set<string>} */
  const rbIds = new Set();
  /** @type {Set<string>} */
  const rcBundles = new Set();

  /**
   * @param {object} item
   */
  const addMeta = (item) => {
    const props = item.properties || {};
    if (props._rb_id) rbIds.add(String(props._rb_id));
    if (props._rc_bundle) rcBundles.add(String(props._rc_bundle));
  };

  for (const item of items) {
    if (keys.has(item.key)) addMeta(item);
  }

  // Recharge parent seed often has no `_rc_bundle` / `_rb_id`. Link children via
  // `_rc_bundle_variant` === parent variant_id.
  for (const item of items) {
    const parentVariant = item.properties?._rc_bundle_variant;
    if (parentVariant == null) continue;
    const linked = items.some(
      (seed) => keys.has(seed.key) && String(seed.variant_id) === String(parentVariant)
    );
    if (!linked) continue;
    keys.add(item.key);
    addMeta(item);
  }

  // Child seed may only include `_rc_bundle` rows. Pull in the hidden parent
  // line via `_rc_bundle_variant` so quantity math reflects bundle box count.
  for (const item of items) {
    if (!keys.has(item.key)) continue;
    const parentVariant = item.properties?._rc_bundle_variant;
    if (parentVariant == null) continue;

    const parentLine = items.find(
      (candidate) =>
        String(candidate.variant_id) === String(parentVariant) &&
        !candidate.properties?._rc_bundle_variant
    );
    if (!parentLine) continue;

    if (!keys.has(parentLine.key)) {
      keys.add(parentLine.key);
      addMeta(parentLine);
    }
  }

  // Parent-only remove with no props: include all current recharge child lines
  // (typical single-bundle cart) so we can discover `_rb_id` for the free gift.
  if (rbIds.size === 0 && rcBundles.size === 0) {
    for (const item of items) {
      if (!item.properties?._rc_bundle) continue;
      keys.add(item.key);
      addMeta(item);
    }
  }

  let changed = true;
  while (changed) {
    changed = false;
    for (const item of items) {
      const props = item.properties || {};
      const rc = props._rc_bundle != null ? String(props._rc_bundle) : '';
      const rb = props._rb_id != null ? String(props._rb_id) : '';
      if ((rc && rcBundles.has(rc)) || (rb && rbIds.has(rb))) {
        if (!keys.has(item.key)) {
          keys.add(item.key);
          changed = true;
        }
        addMeta(item);
      }
    }
  }

  // Free gift / add-ons by `_rb_id`
  for (const item of items) {
    const props = item.properties || {};
    if (!props._rb_id || !rbIds.has(String(props._rb_id))) continue;
    keys.add(item.key);
  }

  // If every `_rc_bundle` line is being removed, clear leftover free gifts.
  const bundleLines = items.filter((item) => item.properties?._rc_bundle);
  const allBundlesRemoved =
    bundleLines.length > 0 && bundleLines.every((item) => keys.has(item.key));
  if (allBundlesRemoved) {
    for (const item of items) {
      if (item.properties?._free_butter || item.properties?._rb_addon) {
        keys.add(item.key);
      }
    }
  }

  // Single free-gift cart + bundle remove with no shared ids
  const freeGifts = items.filter((item) => item.properties?._free_butter);
  const freeGiftAlreadyQueued = freeGifts.some((item) => keys.has(item.key));
  if (!freeGiftAlreadyQueued && freeGifts.length === 1 && keys.size > 0) {
    keys.add(freeGifts[0].key);
  }

  return keys;
}

/**
 * @param {Set<string>|string[]} keys
 * @returns {Record<string, number>}
 */
function updatesFromKeys(keys) {
  /** @type {Record<string, number>} */
  const updates = {};
  for (const key of keys) {
    updates[key] = 0;
  }
  return updates;
}

/**
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

/**
 * @param {any} line
 * @returns {number|null}
 */
function getStoredPerBundleQty(line) {
  const raw = Number.parseInt(String(line?.properties?._pf_bundle_line_qty || ''), 10);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  return raw;
}

/**
 * @param {any} line
 * @param {number} units
 * @returns {number}
 */
function resolvePerBundleQty(line, units) {
  const stored = getStoredPerBundleQty(line);
  if (stored != null) return stored;
  const qty = Math.max(0, Math.round(Number(line?.quantity) || 0));
  return Math.max(1, Math.round(qty / Math.max(1, units)));
}

/**
 * @param {object[]} lines
 * @returns {number}
 */
function inferBundleUnitsFromLines(lines) {
  if (!Array.isArray(lines) || !lines.length) return 1;

  let core = lines.filter((line) => line.properties?._rc_bundle);
  if (!core.length) {
    core = lines.filter((line) => !line.properties?._free_butter && !line.properties?._rb_addon);
  }

  // Preferred path: if we know per-box quantity per line, infer units as qty / per-box-qty.
  const ratioCandidates = core
    .map((line) => {
      const perBundleQty = getStoredPerBundleQty(line);
      const qty = Math.max(0, Math.round(Number(line?.quantity) || 0));
      if (perBundleQty == null || qty <= 0) return null;
      const ratio = qty / perBundleQty;
      return Number.isFinite(ratio) && ratio > 0 ? ratio : null;
    })
    .filter((ratio) => ratio != null)
    .map((ratio) => Number(ratio));

  if (ratioCandidates.length) {
    const rounded = ratioCandidates.map((ratio) => Math.round(ratio));
    const exact = ratioCandidates.every((ratio, idx) => Math.abs(ratio - rounded[idx]) < 0.0001);
    const consistent = rounded.every((value) => value > 0 && value === rounded[0]);
    if (exact && consistent) {
      return rounded[0];
    }
  }

  const parentVariantIds = new Set(
    lines
      .map((line) => line?.properties?._rc_bundle_variant)
      .filter((v) => v != null)
      .map((v) => String(v))
  );

  // Recharge parent line quantity represents bundle box count directly.
  const parentLine = lines.find((line) => {
    const props = line?.properties || {};
    if (props._free_butter || props._rb_addon) return false;

    // Parent can be either a `_rc_bundle` line or a line referenced by
    // child `_rc_bundle_variant` values.
    if (!props._rc_bundle_variant && props._rc_bundle) return true;
    if (!props._rc_bundle_variant && parentVariantIds.size > 0) {
      return parentVariantIds.has(String(line?.variant_id));
    }
    return false;
  });
  const parentQty = Math.round(Number(parentLine?.quantity) || 0);
  if (Number.isFinite(parentQty) && parentQty > 0) {
    return parentQty;
  }

  const qtys = core
    .map((line) => Number(line.quantity) || 0)
    .filter((qty) => Number.isFinite(qty) && qty > 0)
    .map((qty) => Math.round(qty));

  if (!qtys.length) return 1;
  return qtys.reduce((acc, qty) => gcd(acc, qty));
}

/**
 * @param {object|null} cart
 * @param {string} removeHref
 * @returns {number}
 */
function getBundleUnitsFromCart(cart, removeHref) {
  if (!cart?.items?.length || !removeHref) return 1;
  const seedKeys = keysFromRemoveHref(removeHref);
  if (!seedKeys.length) return 1;
  const keys = collectBundleRemoveKeys(cart, seedKeys);
  const lines = cart.items.filter((item) => keys.has(item.key));
  return Math.max(1, inferBundleUnitsFromLines(lines));
}

/**
 * @param {any} cart
 * @param {string[]} seedKeys
 * @returns {{ updates: Record<string, number>, units: number }}
 */
function buildDecreaseBundleUpdates(cart, seedKeys) {
  const keys = collectBundleRemoveKeys(cart, seedKeys);
  const lines = (cart?.items || []).filter((item) => keys.has(item.key));
  const units = inferBundleUnitsFromLines(lines);

  if (units <= 1) {
    return {
      updates: updatesFromKeys(keys),
      units,
    };
  }

  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of lines) {
    const qty = Math.max(0, Math.round(Number(line.quantity) || 0));
    const perBundleQty = resolvePerBundleQty(line, units);
    updates[line.key] = Math.max(0, qty - perBundleQty);
  }

  return { updates, units };
}

/**
 * @param {any} cart
 * @param {string[]} seedKeys
 * @returns {Record<string, number>}
 */
function buildIncreaseBundleUpdates(cart, seedKeys) {
  const keys = collectBundleRemoveKeys(cart, seedKeys);
  const lines = (cart?.items || []).filter((item) => keys.has(item.key));
  const units = inferBundleUnitsFromLines(lines);

  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of lines) {
    const qty = Math.max(0, Math.round(Number(line.quantity) || 0));
    const perBundleQty = resolvePerBundleQty(line, units);
    updates[line.key] = qty + perBundleQty;
  }

  return updates;
}

/**
 * @returns {string[]}
 */
function sectionsToUpdate() {
  const sections = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sections.add(el.dataset.sectionId);
    }
  });
  return [...sections];
}

/**
 * Remove bundle (+ free gift) via Cart API and refresh drawer/page sections.
 * @param {string[]} seedKeys
 * @returns {Promise<void>}
 */
async function removeBundleViaCartApi(seedKeys) {
  const cart = await fetchCartFresh();
  if (!cart || !Array.isArray(cart.items)) throw new Error('Unable to load cart');

  const keys = collectBundleRemoveKeys(cart, seedKeys);
  if (!keys.size) throw new Error('No cart lines to remove');

  const sections = sectionsToUpdate();
  const updateUrl =
    window?.Theme?.routes?.cart_update_url || `${cartRoot()}cart/update.js`;

  /** @type {any} */
  const body = {
    updates: updatesFromKeys(keys),
  };
  if (sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }

  const res = await fetch(updateUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`Cart update failed: ${errBody || res.status}`);
  }

  const parsed = await res.json();
  const itemCount = Number(parsed.item_count) || 0;

  document.dispatchEvent(
    new CartUpdateEvent({}, 'cart-rc-bundle', {
      itemCount,
      source: 'cart-rc-bundle',
      sections: parsed.sections,
    })
  );

  // Fallback if section morph listeners miss this source
  if (!parsed.sections || !sections.length) {
    window.location.href = `${cartRoot()}cart`;
  }
}

/**
 * @param {Record<string, number>} updates
 * @returns {Promise<void>}
 */
async function updateBundleViaCartApi(updates) {
  const sections = sectionsToUpdate();
  const updateUrl = window?.Theme?.routes?.cart_update_url || `${cartRoot()}cart/update.js`;

  /** @type {any} */
  const body = { updates };
  if (sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }

  const res = await fetch(updateUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`Cart update failed: ${errBody || res.status}`);
  }

  const parsed = await res.json();
  const itemCount = Number(parsed.item_count) || 0;

  document.dispatchEvent(
    new CartUpdateEvent({}, 'cart-rc-bundle', {
      itemCount,
      source: 'cart-rc-bundle',
      sections: parsed.sections,
    })
  );

  if (!parsed.sections || !sections.length) {
    window.location.href = `${cartRoot()}cart`;
  }
}

/**
 * @param {string[]} seedKeys
 * @param {1|-1} delta
 * @returns {Promise<void>}
 */
async function changeBundleQuantityViaCartApi(seedKeys, delta) {
  const cart = await fetchCartFresh();
  if (!cart || !Array.isArray(cart.items)) throw new Error('Unable to load cart');

  if (delta > 0) {
    const updates = buildIncreaseBundleUpdates(cart, seedKeys);
    await updateBundleViaCartApi(updates);
    return;
  }

  const { updates } = buildDecreaseBundleUpdates(cart, seedKeys);
  await updateBundleViaCartApi(updates);
}

/**
 * @param {MouseEvent} event
 */
async function onBundleRemoveClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const btn = target.closest('.rc-bundle-qty__btn, .rc-bundle-remove');
  if (!(btn instanceof HTMLElement)) return;
  if (btn.classList.contains('rc-bundle-qty__btn--disabled')) return;
  if (!btn.closest('.cart-items__table-row--rc-bundle')) return;

  const href = btn.dataset.removeHref || btn.getAttribute('href') || '';
  const seedKeys = keysFromRemoveHref(href);
  if (!seedKeys.length && !btn.dataset.removeKeys) return;
  const action = String(btn.dataset.action || (btn.classList.contains('rc-bundle-remove') ? 'remove' : ''));

  event.preventDefault();
  event.stopPropagation();

  if (btn.dataset.pfRemoving === 'true') return;
  btn.dataset.pfRemoving = 'true';
  btn.setAttribute('aria-disabled', 'true');

  try {
    const keys = btn.dataset.removeKeys
      ? String(btn.dataset.removeKeys).split('|').filter(Boolean)
      : seedKeys;
    if (action === 'increase') {
      await changeBundleQuantityViaCartApi(keys, 1);
      const qtyValue = btn.closest('.cart-items__table-row--rc-bundle')?.querySelector('.rc-bundle-qty__value');
      if (qtyValue) {
        const next = (parseInt(qtyValue.textContent || '1', 10) || 1) + 1;
        qtyValue.textContent = String(next);
      }
    } else if (action === 'decrease') {
      await changeBundleQuantityViaCartApi(keys, -1);
      const qtyValue = btn.closest('.cart-items__table-row--rc-bundle')?.querySelector('.rc-bundle-qty__value');
      if (qtyValue) {
        const next = Math.max(0, (parseInt(qtyValue.textContent || '1', 10) || 1) - 1);
        qtyValue.textContent = String(next);
      }
    } else {
      await removeBundleViaCartApi(keys);
    }
  } catch (error) {
    console.error(error);
    // Last resort: navigate with whatever remove href we have
    if (href && href !== '#') window.location.href = href;
  } finally {
    btn.dataset.pfRemoving = 'false';
    btn.removeAttribute('aria-disabled');
  }
}

/**
 * @param {object|null} cart
 * @param {string} removeHref
 * @returns {{ compareMoney: string, discountPct: string, contents: string, frequency: string }|null}
 */
function enrichFromCart(cart, removeHref) {
  if (!cart?.items?.length || !removeHref) return null;

  const keys = keysFromRemoveHref(removeHref);
  if (!keys.length) return null;

  const seed = cart.items.find((item) => keys.includes(item.key));
  if (!seed) return null;

  const bundleId = seed.properties?._rc_bundle;
  const rbId = seed.properties?._rb_id;
  const lines = cart.items.filter((item) => {
    const props = item.properties || {};
    if (bundleId && props._rc_bundle === bundleId) return true;
    if (rbId && String(props._rb_id) === String(rbId) && !props._free_butter && !props._rb_addon) {
      return true;
    }
    return false;
  });
  const contentLines = lines.length ? lines : [seed];

  let finalTotal = 0;
  let compareTotal = 0;
  /** @type {string[]} */
  const contents = [];

  for (const line of contentLines) {
    finalTotal += line.final_line_price || 0;
    const lineCompare =
      line.original_line_price > line.final_line_price
        ? line.original_line_price
        : (line.variant_compare_at_price || 0) * (line.quantity || 1) || line.original_line_price || 0;
    compareTotal += lineCompare > 0 ? lineCompare : line.final_line_price || 0;
    contents.push(`${line.quantity}x ${line.product_title}`);
  }

  let discountPct = '';
  const planName = seed.selling_plan_allocation?.selling_plan?.name || '';
  discountPct = parseDiscountPct(planName);

  if (!discountPct && compareTotal > finalTotal && compareTotal > 0) {
    discountPct = String(Math.round(((compareTotal - finalTotal) / compareTotal) * 100));
  }

  const formatMoney =
    typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function'
      ? (cents) => Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}')
      : (cents) =>
          `$${(cents / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;

  return {
    compareMoney: compareTotal > finalTotal ? formatMoney(compareTotal) : '',
    discountPct,
    contents: contents.join(', '),
    frequency: formatFrequency(planName),
  };
}

/**
 * Store seed keys on bundle action controls.
 * @param {HTMLElement} btn
 * @param {string} href
 */
function bindBundleActionButton(btn, href) {
  const keys = keysFromRemoveHref(href);
  if (keys.length) {
    btn.dataset.removeKeys = keys.join('|');
  }
  if (href && href !== '#') {
    btn.dataset.removeHref = href;
    // Keep a non-navigating placeholder so middle-click / no-js still has a hint
    if (btn.tagName === 'A') {
      btn.setAttribute('href', '#');
      btn.setAttribute('role', 'button');
    }
  }
}

/**
 * @param {HTMLElement} el
 * @param {object|null} [cart]
 */
function enhanceItem(el, cart = null) {
  if (!(el instanceof HTMLElement) || el.dataset.pfRcEnhanced === 'true') return;

  // Theme Liquid already outputs the styled structure
  if (el.querySelector('.rc-bundle-header')) {
    const row = el.closest('.cart-items__table-row--rc-bundle');
    const existingActions = (row || el).querySelectorAll(
      '.rc-bundle-qty__btn[href*="cart/update"], .rc-bundle-qty__btn[data-remove-href], .rc-bundle-remove[href*="cart/update"], .rc-bundle-remove[data-remove-href]'
    );
    existingActions.forEach((actionEl) => {
      if (!(actionEl instanceof HTMLElement)) return;
      const href = actionEl.dataset.removeHref || actionEl.getAttribute('href') || '';
      bindBundleActionButton(actionEl, href);
    });

    const seedAction = (row || el).querySelector(
      '.rc-bundle-qty__btn[data-remove-href], .rc-bundle-qty__btn[href*="cart/update"], .rc-bundle-remove[data-remove-href], .rc-bundle-remove[href*="cart/update"]'
    );
    if (seedAction instanceof HTMLElement) {
      const href = seedAction.dataset.removeHref || seedAction.getAttribute('href') || '';
      const bundleUnits = getBundleUnitsFromCart(cart, href);
      const qtyValue = (row || el).querySelector('.rc-bundle-qty__value');
      if (qtyValue) {
        qtyValue.textContent = String(bundleUnits);
      }
    }

    el.dataset.pfRcEnhanced = 'true';
    removeInjectedStyles(el);
    return;
  }

  const content = el.querySelector('.rc-bundle-content');
  if (!content) return;

  const titleEl = content.querySelector('.rc-bundle-title');
  const contentsEl = content.querySelector('.rc-bundle-contents');
  const priceEl = content.querySelector('.rc-bundle-price');
  const freqEl = content.querySelector('.rc-bundle-frequency');
  const removeLink = [...content.querySelectorAll('a')].find((a) =>
    (a.getAttribute('href') || '').includes('/cart/update')
  );

  const rawTitle = (titleEl?.textContent || 'Build your box').trim();
  const title = rawTitle.split(' - ')[0].trim() || 'Build your box';
  const price = (priceEl?.textContent || '').trim();
  let rawContents = (contentsEl?.textContent || '').replace(/^Contents:\s*/i, '').trim();
  let rawFreq = (freqEl?.textContent || '').trim();
  let frequency = formatFrequency(rawFreq);
  let discountPct = parseDiscountPct(rawFreq);
  let compareMoney = '';
  const removeHref = removeLink?.getAttribute('href') || '#';
  let bundleUnits = getBundleUnitsFromCart(cart, removeHref);

  const enriched = enrichFromCart(cart, removeHref);
  if (enriched) {
    if (enriched.contents) rawContents = enriched.contents;
    if (enriched.frequency) frequency = enriched.frequency;
    if (enriched.discountPct) discountPct = enriched.discountPct;
    if (enriched.compareMoney) compareMoney = enriched.compareMoney;
  }

  const contentLines = rawContents.split(/,\s*/).filter(Boolean);
  const contentsHtml = contentLines
    .map((line, i) => {
      const escaped = escapeHtml(line);
      return i < contentLines.length - 1 ? `${escaped}, <br>` : escaped;
    })
    .join('');

  const seedKeys = keysFromRemoveHref(removeHref);

  content.innerHTML = `
    <div class="rc-bundle-header">
      <p class="rc-bundle-title">${escapeHtml(title)}</p>
      <div class="rc-bundle-prices">
        ${price ? `<span class="rc-bundle-price">${escapeHtml(price)}</span>` : ''}
        ${compareMoney ? `<s class="rc-bundle-compare">${escapeHtml(compareMoney)}</s>` : ''}
      </div>
    </div>
    ${frequency ? `<p class="rc-bundle-frequency">${escapeHtml(frequency)}</p>` : ''}
    ${contentsHtml ? `<div class="rc-bundle-contents">${contentsHtml}</div>` : ''}
    ${
      discountPct
        ? `<p class="rc-bundle-discount">
      <span class="rc-bundle-discount__icon" aria-hidden="true">${CHECKMARK_SVG}</span>
      <span class="rc-bundle-discount__text">${escapeHtml(discountPct)}% Subscription Discount</span>
    </p>`
        : ''
    }
    <div class="rc-bundle-qty" aria-label="Quantity">
      <a
        href="#"
        class="rc-bundle-qty__btn rc-bundle-qty__btn--decrease"
        role="button"
        data-action="decrease"
        data-remove-href="${escapeHtml(removeHref)}"
        data-remove-keys="${escapeHtml(seedKeys.join('|'))}"
        aria-label="Decrease ${escapeHtml(title)} quantity"
      >−</a>
      <span class="rc-bundle-qty__value">${bundleUnits}</span>
      <a
        href="#"
        class="rc-bundle-qty__btn rc-bundle-qty__btn--increase"
        role="button"
        data-action="increase"
        data-remove-href="${escapeHtml(removeHref)}"
        data-remove-keys="${escapeHtml(seedKeys.join('|'))}"
        aria-label="Increase ${escapeHtml(title)} quantity"
      >+</a>
    </div>
    <a
      href="#"
      class="button button--tertiary cart-items__remove rc-bundle-remove"
      role="button"
      data-remove-href="${escapeHtml(removeHref)}"
      data-remove-keys="${escapeHtml(seedKeys.join('|'))}"
      aria-label="Remove ${escapeHtml(title)}"
    >${DELETE_SVG}<span class="visually-hidden">Remove</span></a>
  `;

  removeInjectedStyles(el);
  el.dataset.pfRcEnhanced = 'true';
}

/**
 * @param {ParentNode} [root]
 */
async function enhanceAll(root = document) {
  dedupeBundleRows(root);

  const items = [...root.querySelectorAll('.rc-bundle-item')];
  if (!items.length) return;

  const cart = await fetchCartFresh();

  items.forEach((el) => {
    if (el instanceof HTMLElement) enhanceItem(el, cart);
  });
}

function initCartRcBundle() {
  let scheduled = false;

  const run = () => {
    scheduled = false;
    enhanceAll();
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(run);
  };

  document.addEventListener('click', onBundleRemoveClick, true);

  run();

  const observer = new MutationObserver(schedule);
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartRcBundle);
} else {
  initCartRcBundle();
}
